import React from 'react';

import { Alert, Stack } from '@mui/material';

import DraftSummaryAccordion from './DraftSummaryAccordion';
import type { DraftSummary, SubmissionSectionKey } from './buildDraftSummary';
import { SECTION_STEP_INDEX, applySubmissionStatus, getSubmissionBannerMessage } from './buildDraftSummary';
import type { RollbackErrorEntry } from './rollbackCreatedFlashResources';

interface Props {
  draftSummary: DraftSummary;
  failedStage: SubmissionSectionKey | null;
  hasSucceeded: boolean;
  isFailureActionable: boolean;
  onGoToStep: (stepIndex: number) => void;
  packageLinkFailures: Array<string>;
  rollbackErrors: Array<RollbackErrorEntry>;
  submitErrorMessage: string | null;
}

export default function SubmissionStep({
  draftSummary,
  failedStage,
  hasSucceeded,
  isFailureActionable,
  onGoToStep,
  packageLinkFailures,
  rollbackErrors,
  submitErrorMessage,
}: Props) {
  const annotatedSummary = applySubmissionStatus(draftSummary, {
    failedStage,
    hasSucceeded,
    packageLinkFailures,
  });
  const sections = [
    annotatedSummary.property,
    annotatedSummary.rooms,
    annotatedSummary.ratePlans,
    annotatedSummary.roomRates,
    annotatedSummary.taxesFees,
    annotatedSummary.packages,
  ];

  return (
    <Stack spacing={2}>
      <Alert severity={submitErrorMessage ? 'error' : 'info'}>
        {getSubmissionBannerMessage(submitErrorMessage, isFailureActionable, rollbackErrors.length > 0)}
      </Alert>

      <Stack spacing={1}>
        {sections.map((section) => (
          <DraftSummaryAccordion
            key={section.title}
            section={section}
            errorMessage={section.key === failedStage ? submitErrorMessage : null}
            onGoToStep={
              section.key === failedStage && isFailureActionable
                ? () => onGoToStep(SECTION_STEP_INDEX[section.key])
                : null
            }
            rollbackErrors={rollbackErrors
              .filter((error) => error.section === section.key)
              .map((error) => error.message)}
          />
        ))}
      </Stack>
    </Stack>
  );
}
